
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CertificateNumberInventory } from '@/utils/certificateInventoryTypes';
import { CertificateFilters } from './CertificateFilters';
import { CertificatesInventoryTable } from './CertificatesInventoryTable';
import { CertificateLegend } from './CertificateLegend';
import { Button } from '@/components/ui/button';
import { Download, HelpCircle } from 'lucide-react';
import { EnhancedCertificateExport } from './EnhancedCertificateExport';
import { ExtendedHelp } from './ContextualHelp';
import { useIsMobile } from '@/hooks/use-mobile';
import { ResponsiveTable } from '@/components/ui/responsive-table';

interface CertificateInventoryListProps {
  onRefresh?: () => void;
  onCertificateSelect?: (certificateNumber: string) => void;
}

export function CertificateInventoryList({ onRefresh, onCertificateSelect }: CertificateInventoryListProps) {
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined);
  const [courseTypeFilter, setCourseTypeFilter] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [reportingStatusFilter, setReportingStatusFilter] = useState<string | undefined>(undefined);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['certificateInventory', stateFilter, courseTypeFilter, searchQuery, reportingStatusFilter],
    queryFn: async () => {
      let query = supabase
        .from('certificate_number_inventory')
        .select('*, certificate_reports(*)')
        .order('created_at', { ascending: false });
      
      if (stateFilter) {
        query = query.eq('state', stateFilter);
      }
      
      if (courseTypeFilter) {
        query = query.eq('course_type', courseTypeFilter);
      }
      
      if (reportingStatusFilter) {
        query = query.eq('reporting_status', reportingStatusFilter);
      }
      
      if (searchQuery) {
        query = query.ilike('certificate_number', `%${searchQuery}%`);
      }
      
      const { data, error } = await query.limit(100);
      
      if (error) throw error;
      return data as CertificateNumberInventory[];
    },
  });
  
  const handleRefresh = () => {
    refetch();
    if (onRefresh) onRefresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Certificate Inventory</h2>
          <ExtendedHelp 
            topic="certificate-export" 
            variant="icon" 
            size="md"
          />
        </div>
        
        <Button 
          variant="outline" 
          onClick={() => setIsExportDialogOpen(true)}
          className="flex gap-2 items-center"
          size={isMobile ? "sm" : "default"}
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>
      
      <CertificateFilters 
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
        courseTypeFilter={courseTypeFilter}
        setCourseTypeFilter={setCourseTypeFilter}
        reportingStatusFilter={reportingStatusFilter}
        setReportingStatusFilter={setReportingStatusFilter}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onRefresh={handleRefresh}
      />

      <ResponsiveTable compact={isMobile}>
        <CertificatesInventoryTable 
          data={data} 
          isLoading={isLoading} 
          error={error as Error | null}
          onCertificateSelect={onCertificateSelect}
        />
      </ResponsiveTable>
      
      <CertificateLegend />
      
      {/* Enhanced Export Dialog */}
      <EnhancedCertificateExport
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
      />
    </div>
  );
}
